import { refreshAccessToken } from "../api";
import { getAuthInfosFromCookie, logout } from "../utils";
import { extractAuthContextFromJWT } from "../lib/jwt";
import { store } from "../store";
import { setAuthCookie } from "../utils";
import {
  setJwtToken,
  setRefreshToken,
  setExpiresIn,
  setTokenCreatedAt,
  setAuthToken,
  setAuthContext,
} from "../store";

let refreshPromise = null;
let signingOut = false;

// Renew shortly before the access token actually expires. Without this margin a request
// issued just before the boundary reaches gopeople after it and comes back 401.
const REFRESH_MARGIN_MS = 60 * 1000;

// This runs inside the request interceptor, which rejects before anything is sent, so
// the 401 handler in goPeopleAxios never gets a response to react to and nobody signs
// the user out. Do it here, with the same idiom that handler uses, instead of leaving
// the app stuck on a loader. "/" renders Home, which already handles the signed out
// case, so do not reload it and loop.
const signOut = () => {
  if (signingOut) {
    return;
  }
  signingOut = true;
  logout(false);

  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
};

export const getValidGoPeopleToken = async (forceRefresh = false) => {
  const auth = getAuthInfosFromCookie();
  if (!auth) {
    signOut();
    throw new Error("No GoPeople auth stored");
  }
  const createdAt = Number(auth.createdAt);
  const expiresIn = Number(auth.expiresIn);

  const expiresAt = createdAt * 1000 + expiresIn * 1000;
  const isTokenValid =
    auth.jwtToken && expiresAt > Date.now() + REFRESH_MARGIN_MS;

  if (!forceRefresh && isTokenValid) {
    return auth.jwtToken;
  }

  if (!auth.refreshToken) {
    signOut();
    throw new Error("No GoPeople refresh token stored");
  }

  if (refreshPromise) {
    const tokens = await refreshPromise;
    return tokens.accessToken;
  }

  refreshPromise = (async () => {
    try {
      const newTokens = await refreshAccessToken(auth.refreshToken);

      const createdAt = Math.floor(Date.now() / 1000);
      const authC = extractAuthContextFromJWT(newTokens.accessToken);

      // JWT `exp` is an absolute Unix timestamp (seconds), not a duration.
      let dtExpire = new Date(authC.expiresAt * 1000);

      const dispatch = store.dispatch;

      if (authC) {
        // setAuthContext(authC);
        dispatch(setAuthToken(authC.ttpAccessToken));
      }
      dispatch(setExpiresIn(authC.expiresAt));
      dispatch(setTokenCreatedAt(authC.createdAt));
      // dispatch(setAuthScope(newTokens.scope));
      dispatch(setJwtToken(newTokens.accessToken));
      dispatch(setRefreshToken(newTokens.refreshToken));

      if (authC?.ttpAccessToken) {
        dispatch(setAuthToken(authC.ttpAccessToken));
      }
      
      const updatedAuth = {
        ...auth,
        jwt: newTokens.accessToken,
        jwtToken: newTokens.accessToken,
        refreshToken: newTokens.refreshToken,
        expiresIn: authC.expiresAt - createdAt,
        createdAt,
      };

      setAuthCookie(JSON.stringify(updatedAuth), dtExpire);

      return newTokens;
    } catch (error) {
      // The refresh token is gone or gopeople rejected it: nothing left to renew from.
      signOut();
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  const tokens = await refreshPromise;
  return tokens.accessToken;
};
